import { Fragment } from "react";
import { Link } from "react-router-dom";
import { ExternalLink } from "lucide-react";
import analytics from "@/utils/analytics";

/**
 * Renders approved guide copy containing `**bold**`, `[label](/path)` and
 * `[label](https://…)` markup (see data/guidesContent.js).
 *
 * Every link fires guide_link_click (comparison-guides-v1) with a
 * controlled link_role derived from its destination and the section's
 * cta_position. Internal destinations are client-side <Link>s so SPA
 * navigation (and the single page_view in ScrollToTop) is preserved.
 *
 * Output alternates text and element nodes — never two adjacent text
 * nodes — so the react-snap snapshot hydrates cleanly under React 19.
 */

const LINK_CLASS =
  "font-semibold text-brand-aqua-deep underline underline-offset-2 hover:text-brand-black transition-colors";

export function guideLinkRole(href) {
  if (/^https?:\/\//.test(href)) return "provider_source";
  if (href === "/statement-review") return "statement_review";
  if (href === "/payment-finder") return "payment_finder";
  return "internal_resource";
}

export function GuideLink({ href, guideId, position, className = LINK_CLASS, children, testId }) {
  const role = guideLinkRole(href);
  const onClick = () => analytics.guideLinkClick(guideId, role, position);
  if (role === "provider_source") {
    return (
      <a href={href} target="_blank" rel="noopener noreferrer" onClick={onClick} className={className} data-testid={testId} data-measurement-owned="guide">
        {children}
        <ExternalLink size={12} className="inline-block ml-0.5 -mt-0.5" aria-hidden="true" />
        <span className="sr-only">(opens in a new tab)</span>
      </a>
    );
  }
  return (
    <Link to={href} onClick={onClick} className={className} data-testid={testId} data-measurement-owned="guide">
      {children}
    </Link>
  );
}

const LINK_RE = /(\[[^\]]+\]\([^)]+\))/g;
const BOLD_RE = /(\*\*[^*]+\*\*)/g;

function renderLinks(text, ctx, keyPrefix) {
  let offset = 0;
  return String(text)
    .split(LINK_RE)
    .filter(Boolean)
    .map((part) => {
      const key = `${keyPrefix}-${offset}`;
      offset += part.length;
      const m = part.match(/^\[([^\]]+)\]\(([^)]+)\)$/);
      if (!m) return <Fragment key={key}>{part}</Fragment>;
      return (
        <GuideLink key={key} href={m[2]} guideId={ctx.guideId} position={ctx.position}>
          {m[1]}
        </GuideLink>
      );
    });
}

export default function GuideCopy({ text, guideId, position }) {
  const ctx = { guideId, position };
  let offset = 0;
  return (
    <>
      {String(text || "")
        .split(BOLD_RE)
        .filter(Boolean)
        .map((part) => {
          const key = `${offset}`;
          offset += part.length;
          const m = part.match(/^\*\*([^*]+)\*\*$/);
          if (m) {
            return (
              <strong key={key} className="font-semibold text-brand-black">
                {renderLinks(m[1], ctx, key)}
              </strong>
            );
          }
          return <Fragment key={key}>{renderLinks(part, ctx, key)}</Fragment>;
        })}
    </>
  );
}
